import React, { useState } from 'react';
import { useI18n } from '../../hooks/useI18n';
import { Consultation } from '../../types/cabinet.types';
import { Button } from '../ui/Button';

import doctorOlha from '@/assets/image/olga_dok.png';
import dietetyk_wroclaw from '@/assets/image/dietetyk_wroclaw.png';


import DoctorFemaleUrl from '@/assets/image/iconFemale.svg';
import CalendarIcon from '@/assets/image/calendar-days.svg?react';
import WeightIcon from '@/assets/image/weight.svg?react';


interface ConsultationCardProps {
  consultation: Consultation;
  onRescheduleClick?: () => void;
  onRebookClick?: () => void;
  onRecommendationsClick?: () => void;
  onSummaryClick?: () => void;
  className?: string;
}

export const ConsultationCard: React.FC<ConsultationCardProps> = ({
  consultation,
  onRescheduleClick,
  onRebookClick,
  onRecommendationsClick,
  onSummaryClick,
  className = ''
}) => {
  const { t } = useI18n();
  const [isDownloading, setIsDownloading] = useState(false);
  const [isMeetActive, setIsMeetActive] = useState(false);

  React.useEffect(() => {
    if (consultation.status !== 'upcoming' || !consultation.timestamp) return;
    
    const checkActive = () => {
      const nowMs = Date.now();
      const consultMs = consultation.timestamp! * 1000;
      // 24 hours = 24 * 60 * 60 * 1000 = 86400000 ms
      if (consultMs - nowMs <= 86400000) {
        setIsMeetActive(true);
      } else {
        setIsMeetActive(false);
      }
    };

    checkActive();
    const interval = setInterval(checkActive, 60000);
    return () => clearInterval(interval);
  }, [consultation.status, consultation.timestamp]);

  const handleDownloadPWA = async (url: string, filename: string) => {
    try {
      setIsDownloading(true);
      const finalFilename = filename.toLowerCase().endsWith('.pdf') ? filename : `${filename}.pdf`;

      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      const pdfBlob = new Blob([blob], { type: 'application/pdf' });

      // 1) Web Share API — єдиний надійний спосіб на iOS PWA ("Зберегти у файли")
      if (typeof navigator.share === 'function' && navigator.canShare) {
        const file = new File([pdfBlob], finalFilename, { type: 'application/pdf' });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], title: finalFilename });
          return;
        }
      }

      // 2) Blob URL + anchor — для Android PWA / desktop
      const blobUrl = window.URL.createObjectURL(pdfBlob);
      const link = window.document.createElement('a');
      link.href = blobUrl;
      link.download = finalFilename;
      link.target = '_self'; // '_self' надійніше ніж '_blank' в iOS standalone
      link.style.display = 'none';
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      // Відкликаємо blob URL з затримкою — iOS потребує часу на читання
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 200);
    } catch (error) {
      console.error('Error downloading file in PWA', error);
      // 3) Останній fallback: відкрити у поточному вікні (iOS може показати Share sheet)
      window.location.href = url;
    } finally {
      setIsDownloading(false);
    }
  };

  const isUpcoming  = consultation.status === 'upcoming';
  const isCancelled = consultation.status === 'cancelled';
  const isPending   = consultation.status === 'pending';
  const isNieudane  = consultation.status === 'nieudane';

  // Визначаємо тип картки. Fallback — 'consultation'
  const cardType = consultation.cardType ?? 'consultation';

  // Avatar mapping
  const getDoctorAvatar = (fileName: string, importedImage: string) => {
    if (typeof window !== 'undefined' && window.cabinetVars?.siteUrl) {
      const basePath = import.meta.env.DEV ? 'src/assets/image' : 'build/static';
      return `${window.cabinetVars.siteUrl.replace(/\/$/, '')}/wp-content/plugins/custom-api/react/${basePath}/${fileName}`;
    }
    return importedImage;
  };

  const getAvatarUrl = () => {
    if (consultation.doctorAvatar) return consultation.doctorAvatar;
    if (consultation.doctorName) {
      
      if (consultation.doctorName.includes('Kompaniiets Olha') || consultation.doctorName.includes('Olha Kompaniiets')) {
        return getDoctorAvatar('olga_dok.png', doctorOlha);
      }
      if (consultation.doctorName.includes('Harmash Daria') || consultation.doctorName.includes('Smarovoz Anastasiia')) {
        return getDoctorAvatar('iconFemale.svg', DoctorFemaleUrl);
      }
    }
    return null;
  };

  const avatarUrl = getAvatarUrl();

  const formattedWeight = consultation.weight?.toString().replace('.', ',');

  // ─── Статусний бейдж (спільний для всіх типів) ───────────────────────
  const StatusBadge = (
    <div className="cab-cons-header">
      {isUpcoming  && <span className="cab-status-pill cab-pill-upcoming">{t('status_upcoming')}</span>}
      {!isUpcoming && !isCancelled && !isPending && !isNieudane && <span className="cab-status-pill cab-pill-past">{t('status_done')}</span>}
      {isCancelled && <span className="cab-status-pill cab-pill-cancelled">{t('status_cancelled') || 'Anulowane'}</span>}
      {isPending && <span className="cab-status-pill cab-pill-pending">{t('status_pending') || 'Oczekuję na płatność'}</span>}
      {isNieudane && <span className="cab-status-pill cab-pill-nieudane">{t('status_nieudane') || 'Nieudane'}</span>}
    </div>
  );

  // ─── Блок лікаря (Тип 1 і Тип 2) ────────────────────────────────────
  const isDietService = consultation.serviceName && (
    consultation.serviceName.includes('Jadłospis') || 
    consultation.serviceName.includes('dietetyk') || 
    consultation.serviceName.includes('Pakiet: konsultacja')
  );

  const DoctorRow = (
    <div className="cab-doc-row">
      <div className="cab-doc-photo">
        {avatarUrl ? (
          <img src={avatarUrl} alt={consultation.doctorName} />
        ) : isDietService ? (
          <img src={getDoctorAvatar('dietetyk_wroclaw.png', dietetyk_wroclaw)} alt={t('doctor_alt') || 'Lekarz'} />
        ) : (
          <img src={getDoctorAvatar('iconFemale.svg', DoctorFemaleUrl)} alt={t('doctor_alt') || 'Lekarz'} />
        )}
      </div>
      <div className="cab-doc-details">
        <div className="cab-doc-name">
          {isDietService 
            ? 'Joanna Adamczyk' 
            : (consultation.doctorName && consultation.doctorName !== 'Dowolny lekarz' 
                ? `${t('dr_prefix') || 'Lek.'} ${consultation.doctorName}` 
                : (consultation.doctorName || (t('any_doctor') || 'Dowolny lekarz'))
              )
          }
        </div>
        <div className="cab-doc-spec">{isDietService ? consultation.serviceName : consultation.doctorSpecialty}</div>
      </div>
    </div>
  );

  // ─── Рядок № замовлення (спільний) ───────────────────────────────────
  const OrderRow = (
    <div className="cab-info-row">
      <span className="cab-info-text cab-order-text">
        {t('order_no') || 'Nr zamówienia'} <b>#{consultation.orderId}</b>
      </span>
    </div>
  );

  // ════════════════════════════════════════════════════════════════
  // ТИП 3 — Package (Synevo)
  // ════════════════════════════════════════════════════════════════
  if (cardType === 'package') {
    const hasPdf = !!(consultation.skierowanieUrl);
    return (
      <div className={`cab-cons ${className}`}>
        {StatusBadge}

        <div className="cab-doc-row">
          <div className="cab-doc-photo cab-synevo-photo">
            <div className="cab-synevo-icon">
              <svg width="100%" height="100%" viewBox="0 0 72 72" fill="none" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink">
                <rect x="0.5" y="0.5" width="71" height="71" rx="35.5" fill="url(#pattern0_2299_51990)" stroke="#AB9CC9"/>
                <defs>
                  <pattern id="pattern0_2299_51990" patternContentUnits="objectBoundingBox" width="1" height="1">
                    <use xlinkHref="#image0_2299_51990" transform="scale(0.00444444)"/>
                  </pattern>
                  <image id="image0_2299_51990" width="225" height="225" preserveAspectRatio="none" xlinkHref="data:image/png;base64,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"/>
                </defs>
              </svg>
            </div>
          </div>
          <div className="cab-doc-details">
            <div className="cab-doc-name">{t('synevo_package') || 'Pakiet badań w Synevo'}</div>
            <div className="cab-doc-spec">{consultation.serviceName || (t('blood_tests_package') || 'Pakiet badań krwi')}</div>
          </div>
        </div>

        <div className="cab-separator" />

        <div className="cab-info-list">
          <div className="cab-info-row">
            <div className="cab-ic cab-synevo-location-ic">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                <circle cx="12" cy="10" r="3"/>
              </svg>
            </div>
            <span className="cab-info-text">{t('synevo_any_point')}</span>
          </div>
          {OrderRow}
        </div>

        <div className="cab-btn-row">
          {consultation.checkupResultsReady && consultation.typeformUrl ? (
            <a
              href={consultation.typeformUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="cab-btn-flex cab-btn-resched cab-synevo-link cab-checkup-results-btn"
            >
              {t('cons_checkup_results_btn') || 'Wybierz termin rozmowy'}
            </a>
          ) : (
            <a
              href={t('cons_find_point_link') || 'https://tabletka24.pl/map-synevo/'}
              target="_blank"
              rel="noopener noreferrer"
              className="cab-btn-flex cab-btn-resched cab-synevo-link"
            >
              {t('cons_find_point')}
            </a>
          )}
          {hasPdf && (
            <a
              href={consultation.skierowanieUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="cab-btn-flex cab-btn-rebook cab-synevo-link"
              onClick={(e) => {
                const isStandalone = window.matchMedia('(display-mode: standalone)').matches || ('standalone' in navigator && (navigator as any).standalone === true);
                if (isStandalone) {
                  e.preventDefault();
                  e.stopPropagation();
                  if (consultation.skierowanieUrl) {
                    handleDownloadPWA(consultation.skierowanieUrl, 'Skierowanie');
                  }
                } else {
                  e.stopPropagation();
                }
              }}
              style={isDownloading ? { opacity: 0.7, pointerEvents: 'none' } : undefined}
            >
              {isDownloading ? '...' : t('cons_download_referral')}
            </a>
          )}
        </div>


        <CardStyles />
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════════
  // ТИП 2 — Service (E-recepta, Antykoncepcja, E-zwolnienie L4)
  // Без дати/часу, без кнопки «Przełóż»
  // ════════════════════════════════════════════════════════════════
  
  if (cardType === 'service') {
    return (
      <div 
        className={`cab-cons ${className} ${!isUpcoming && !isCancelled && !isPending && !isNieudane && onSummaryClick ? 'cab-cons-clickable' : ''}`}
        onClick={() => {
          if (!isUpcoming && !isCancelled && !isPending && !isNieudane && onSummaryClick) {
            onSummaryClick();
          }
        }}
      >
        {StatusBadge}
        {DoctorRow}
        <div className="cab-separator" />

        <div className="cab-info-list">
          {consultation.sickNoteFromDate && consultation.sickNoteToDate && (
            <div className="cab-info-row">
              <div className="cab-ic">
                <CalendarIcon />
              </div>
              <span className="cab-info-text" dangerouslySetInnerHTML={{
                __html: t('sick_note_from_to', {
                  from: consultation.sickNoteFromDate?.split('-').reverse().join('.') || '',
                  to: consultation.sickNoteToDate?.split('-').reverse().join('.') || ''
                })
              }} />
            </div>
          )}
          {OrderRow}
        </div>

        <div className="cab-btn-row">
          {!isUpcoming && !isCancelled && !isPending && !isNieudane ? (
            <Button variant="ghost" onClick={(e) => { e.stopPropagation(); onSummaryClick?.(); }} className="cab-btn-flex cab-btn-recs">
              {t('visit_summary')}
            </Button>
          ) : isPending && consultation.paymentUrl ? (
            <Button as="a" href={consultation.paymentUrl} variant="primary" className="cab-btn-flex" onClick={(e: any) => e.stopPropagation()}>
              {t('pay_for_visit') || 'Opłać wizytę'}
            </Button>
          ) : isNieudane ? (
            <a
              href="mailto:kontakt@tabletka24.pl"
              className="cab-btn-flex cab-btn-support"
              onClick={(e) => e.stopPropagation()}
            >
              {t('contact_support') || 'Skontaktuj się z nami'}
            </a>
          ) : (
            !!consultation.skierowanieUrl && (
              <a
                href={consultation.skierowanieUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="cab-btn-flex cab-btn-rebook cab-synevo-link"
                onClick={(e) => {
                  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || ('standalone' in navigator && (navigator as any).standalone === true);
                  if (isStandalone) {
                    e.preventDefault();
                    e.stopPropagation();
                    handleDownloadPWA(consultation.skierowanieUrl!, 'Skierowanie');
                  } else {
                    e.stopPropagation();
                  }
                }}
                style={isDownloading ? { opacity: 0.7, pointerEvents: 'none' } : undefined}
              >
                {isDownloading ? '...' : t('cons_download_referral')}
              </a>
            )
          )}
          {!isPending && !isNieudane && (consultation.productUrl ? (
            <Button as="a" href={consultation.productUrl} variant="primary" className="cab-btn-flex cab-btn-rebook" onClick={(e: any) => e.stopPropagation()}>
              {t('rebook')}
            </Button>
          ) : (
            <Button variant="primary" onClick={(e) => { e.stopPropagation(); onRebookClick?.(); }} className="cab-btn-flex cab-btn-rebook">
              {t('rebook')}
            </Button>
          ))}
        </div>

        <CardStyles />
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════════
  // ТИП 1 — Consultation (Teleporada / Otyłość першa/контрольна)
  // Повна картка: фото лікаря, дата, вага (якщо є), Przełóż + Umów ponownie
  // ════════════════════════════════════════════════════════════════
  return (
    <div 
      className={`cab-cons ${className} ${!isUpcoming && !isCancelled && !isPending && !isNieudane && onSummaryClick ? 'cab-cons-clickable' : ''}`}
      onClick={() => {
        if (!isUpcoming && !isCancelled && !isPending && !isNieudane && onSummaryClick) {
          onSummaryClick();
        }
      }}
    >
      {StatusBadge}
      {DoctorRow}
      <div className="cab-separator" />

      <div className="cab-info-list">
        {consultation.date && (
          <div className="cab-info-row">
            <div className="cab-ic">
              <CalendarIcon />
            </div>
            <span className="cab-info-text">{consultation.date}</span>
          </div>
        )}

        

        {OrderRow}

        {consultation.weight && (
          <div className="cab-info-row">
            <div className={`cab-weight-pill ${consultation.previousWeight !== undefined && consultation.weight < consultation.previousWeight ? 'cab-weight-pill-success' : ''}`}>
              <span className="cab-weight-ic">
                <WeightIcon />
              </span>
              <span>{t('weight')}: {formattedWeight} kg</span>
              {consultation.previousWeight !== undefined && consultation.weight < consultation.previousWeight && (
                <span className="cab-weight-trend cab-weight-trend-down">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="5" x2="12" y2="19"></line>
                    <polyline points="19 12 12 19 5 12"></polyline>
                  </svg>
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="cab-btn-row">
        {isUpcoming && consultation.rescheduleAvailable && (
          <Button variant="ghost" onClick={(e) => { e.stopPropagation(); onRescheduleClick?.(); }} className="cab-btn-flex cab-btn-resched">
            {t('reschedule')}
          </Button>
        )}
        {!isUpcoming && !isCancelled && !isPending && !isNieudane ? (
          <Button variant="ghost" onClick={(e) => { e.stopPropagation(); onSummaryClick?.(); }} className="cab-btn-flex cab-btn-recs">
            {t('visit_summary')}
          </Button>
        ) : isPending && consultation.paymentUrl ? (
          <Button as="a" href={consultation.paymentUrl} variant="primary" className="cab-btn-flex" onClick={(e: any) => e.stopPropagation()}>
            {t('pay_for_visit') || 'Opłać wizytę'}
          </Button>
        ) : isNieudane ? (
          <a
            href="mailto:kontakt@tabletka24.pl"
            className="cab-btn-flex cab-btn-support"
            onClick={(e) => e.stopPropagation()}
          >
            {t('contact_support') || 'Skontaktuj się z nami'}
          </a>
        ) : (
          !!consultation.skierowanieUrl && (
            <a
              href={consultation.skierowanieUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="cab-btn-flex cab-btn-rebook cab-synevo-link"
              onClick={(e) => {
                const isStandalone = window.matchMedia('(display-mode: standalone)').matches || ('standalone' in navigator && (navigator as any).standalone === true);
                if (isStandalone) {
                  e.preventDefault();
                  e.stopPropagation();
                  handleDownloadPWA(consultation.skierowanieUrl!, 'Skierowanie');
                } else {
                  e.stopPropagation();
                }
              }}
              style={isDownloading ? { opacity: 0.7, pointerEvents: 'none' } : undefined}
            >
              {isDownloading ? '...' : t('cons_download_referral')}
            </a>
          )
        )}
        {!isPending && !isNieudane && (
          isUpcoming && isDietService && consultation.meet_link ? (
            isMeetActive ? (
              <Button
                as="a"
                href={consultation.meet_link}
                target="_blank"
                rel="noopener noreferrer"
                variant="primary"
                className="cab-btn-flex cab-btn-rebook"
                onClick={(e: any) => e.stopPropagation()}
              >
                Dołącz
              </Button>
            ) : (
              <Button
                variant="primary"
                className="cab-btn-flex cab-btn-rebook"
                disabled={true}
                style={{ opacity: 0.7, cursor: 'not-allowed', backgroundColor: '#A48FC6', borderColor: '#A48FC6' }}
                onClick={(e: any) => e.stopPropagation()}
              >
                Dołącz — aktywne 24h przed
              </Button>
            )
          ) : (
            consultation.productUrl ? (
              <Button as="a" href={consultation.productUrl} variant="primary" className="cab-btn-flex cab-btn-rebook" onClick={(e: any) => e.stopPropagation()}>
                {t('rebook')}
              </Button>
            ) : (
              <Button variant="primary" onClick={(e) => { e.stopPropagation(); onRebookClick?.(); }} className="cab-btn-flex cab-btn-rebook">
                {t('rebook')}
              </Button>
            )
          )
        )}
      </div>

      <CardStyles />
    </div>
  );
};

// ─── Спільні стилі (виокремлено щоб не дублювати) ───────────────────────────
const CardStyles: React.FC = () => (
  <style>{`
    .cab-cons {
      position: relative;
      background: #fff;
      border-radius: 24px;
      padding: 20px;
      border: 1px solid var(--cab-purple-main, #E6D6FA);
      text-align: left;
      font-family: 'Poppins', sans-serif;
      box-sizing: border-box;
      display: flex;
      flex-direction: column;
      gap: 16px;
    }
    .cab-cons-clickable {
      cursor: pointer;
      transition: transform 0.2s, box-shadow 0.2s;
    }
    .cab-cons-clickable:hover {
      box-shadow: 0 4px 12px rgba(54, 23, 119, 0.08);
    }
    .cab-cons-header {
      margin-bottom: 2px;
    }
    .cab-status-pill {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 4px 14px;
      border-radius: 100px;
      font-size: 13px;
      font-weight: 500;
      font-family: 'Poppins', sans-serif;
    }
    .cab-pill-upcoming {
      background: var(--cab-violet, #361777);
      color: #fff;
    }
    .cab-pill-past {
      background: var(--cab-d6, #D6C1F1);
      color: #fff;
    }
    .cab-pill-cancelled {
      background: #FFEBEE;
      color: #C62828;
      border: none;
    }
    .cab-pill-pending {
      background: #FFF3E0;
      color: #E65100;
      border: none;
    }
    .cab-pill-nieudane {
      background: #FCE4EC;
      color: #AD1457;
      border: none;
    }
    .cab-btn-support {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 10px 20px;
      border-radius: 100px;
      border: 1.5px solid #AD1457;
      color: #AD1457;
      font-family: 'Poppins', sans-serif;
      font-weight: 500;
      font-size: 14px;
      text-decoration: none;
      cursor: pointer;
      transition: background 0.2s;
    }
    .cab-btn-support:hover {
      background: #FCE4EC;
    }
    .cab-separator {
      height: 1px;
      background: var(--cab-purple-main, #E6D6FA);
      width: 100%;
      margin: 4px 0;
    }
    .cab-doc-row {
      display: flex;
      gap: 16px;
    }
    .cab-doc-photo {
      width: 64px;
      height: 64px;
      border-radius: 100px;
      background: linear-gradient(135deg, var(--cab-purple-main, #E6D6FA), var(--cab-d6, #D6C1F1));
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 700;
      color: var(--cab-violet, #361777);
      font-size: 17px;
      flex-shrink: 0;
      overflow: hidden;
    }
    
     

    .cab-doc-photo img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
    .cab-synevo-photo {
      background: transparent;
      border: none;
    }
    .cab-synevo-icon {
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .cab-doc-details {
      display: flex;
      flex-direction: column;
      gap: 4px;
      justify-content: center;
    }
    .cab-doc-name {
      font-size: 16px;
      font-weight: 600;
      color: var(--cab-violet, #361777);
      line-height: 1.2em;
    }
    .cab-doc-spec {
      font-size: 14px;
      font-weight: 500;
      color: var(--cab-body, #57437E);
      line-height: 1.4em;
    }
    .cab-info-list {
      display: flex;
      flex-direction: column;
      gap: 16px;
      flex-grow: 1;
    }
    .cab-info-row {
      display: flex;
      align-items: center;
      gap: 12px;
    }
    .cab-info-text {
      font-size: 16px;
      font-weight: 500;
      color: var(--cab-body, #57437E);
      line-height: 1.4em;
    }
    .cab-info-text b {
      font-weight: 600;
    }
    .cab-order-text, .cab-order-text b {
      color: var(--cab-violet, #361777);
    }
    .cab-ic {
      width: 24px;
      height: 24px;
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .cab-ic svg {
      width: 100%;
      height: 100%;
    }
    .cab-synevo-location-ic {
      color: var(--cab-violet, #361777);
    }
    .cab-weight-pill {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      background: var(--cab-purple-main, #E6D6FA);
      color: #fff;
      font-weight: 600;
      font-size: 15px;
      padding: 8px 16px;
      border-radius: 100px;
      line-height: 1.4em;
    }
    .cab-weight-pill-success {
      background: #E8F5E9;
      color: #2E7D32;
    }
    .cab-weight-ic {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 18px;
      height: 18px;
    }
    .cab-weight-ic svg {
      width: 100%;
      height: 100%;
    }
    .cab-weight-trend {
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .cab-weight-trend-down {
      color: currentColor;
    }
    .cab-btn-row {
      display: flex;
      flex-direction: column;
      gap: 12px;
      margin-top: 8px;
    }
    .cab-synevo-link {
      text-decoration: none !important;
      cursor: pointer;
      display: flex !important;
      align-items: center;
      justify-content: center;
      box-sizing: border-box;
      font-family: 'Poppins', sans-serif;
    }
    .cab-btn-flex {

      width: 100% !important;
      border-radius: 100px !important;
      padding: 12px 24px !important;
      font-size: 15px !important;
      font-weight: 500 !important;
      font-family: 'Poppins', sans-serif !important;
      justify-content: center !important;
      height: auto !important;
      text-align: center !important;
      text-decoration: none !important;
      display: flex !important;
      align-items: center !important;
    }
    .cab-btn-resched {
      border: 1px solid var(--cab-violet, #361777) !important;
      color: var(--cab-violet, #361777) !important;
      background: #fff !important;
    }
    .cab-btn-rebook {
      background: var(--cab-violet, #361777) !important;
      color: #fff !important;
    }

    @media (min-width: 1024px) {
      .cab-cons {
        padding: 24px;
      }
    }

    @media (min-width: 1024px) {
      .cab-cons {
        flex-direction: row;
        justify-content: space-between;
        align-items: flex-start;
        padding: 24px 32px;
        gap: 32px;
      }
      .cab-cons-header {
        display: none;
      }
      .cab-doc-row {
        flex: 1.2;
        min-width: 0;
      }
      .cab-separator {
        display: none;
      }
      .cab-info-list {
        flex: 1.5;
        min-width: 0;
        gap: 12px;
      }
      .cab-btn-row {
        width: 220px;
        flex-shrink: 0;
        
      }
    }
  `}</style>
);
